import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ArrowRight, MessageSquare, Brain, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { pickBg } from "@/assets/generated";
import { Reveal, RevealStagger } from "@/components/vision/reveal";
import { EditorialHeading } from "./editorial-heading";

export function HowItWorksSection() {
  const { t } = useTranslation();
  const steps = [
    { icon: MessageSquare, title: t("howItWorks.items.ask.title"), desc: t("howItWorks.items.ask.desc") },
    { icon: Brain, title: t("howItWorks.items.analyze.title"), desc: t("howItWorks.items.analyze.desc") },
    { icon: Sparkles, title: t("howItWorks.items.results.title"), desc: t("howItWorks.items.results.desc") },
  ];
  return (
    <section className="relative overflow-hidden bg-background py-16 sm:py-24">
      <img src={pickBg(20)} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-10 dark:opacity-20" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-b from-background via-background/80 to-background" />
      <div className="relative mx-auto max-w-6xl px-5 sm:px-6 lg:px-8">
        <EditorialHeading
          index="No. 05"
          eyebrow={t("howItWorks.eyebrow")}
          title={t("howItWorks.title")}
        />
        <RevealStagger className="mt-10 grid gap-8 sm:mt-16 md:grid-cols-3" stagger={0.15} y={32} blur={8}>
          {steps.map((s, i) => (
            <div key={s.title} className="relative">
              {i < steps.length - 1 && (
                <div className="hidden md:block absolute top-8 left-[calc(100%-1rem)] w-[calc(100%-3rem)] h-0.5 bg-gradient-to-r from-border to-transparent" />
              )}
              <div className="group relative flex flex-col items-center text-center">
                <div className="relative">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg sm:h-16 sm:w-16 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-6">
                    <s.icon className="h-6 w-6 sm:h-7 sm:w-7" />
                  </div>
                  <div className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-accent font-display text-xs font-bold text-accent-foreground shadow-md sm:h-7 sm:w-7 sm:text-sm">
                    {i + 1}
                  </div>
                </div>
                <h3 className="mt-5 font-display text-lg font-semibold sm:mt-6 sm:text-xl">{s.title}</h3>
                <p className="mt-2 max-w-xs text-[14px] text-muted-foreground sm:text-sm">{s.desc}</p>
              </div>
            </div>
          ))}
        </RevealStagger>
      </div>
    </section>
  );
}

export function FinalCTASection() {
  const { t } = useTranslation();
  return (
    <section className="relative overflow-hidden bg-black py-20 sm:py-28">
      <img src={pickBg(8)} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-25" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/80 to-black" />
      <div aria-hidden className="aurora-orb pointer-events-none absolute -left-32 top-0 h-[520px] w-[520px]" style={{ background: "radial-gradient(circle at 30% 30%, hsl(var(--aurora-1) / 0.55), transparent 60%)" }} />
      <div aria-hidden className="aurora-orb pointer-events-none absolute -right-32 bottom-0 h-[460px] w-[460px]" style={{ background: "radial-gradient(circle at 30% 30%, hsl(var(--aurora-3) / 0.5), transparent 60%)", animationDelay: "5s" }} />
      <div className="relative mx-auto max-w-4xl px-5 text-center sm:px-6 lg:px-8">
        <Reveal y={32} blur={12} duration={1}>
          <h2 className="font-display text-[28px] font-bold leading-[1.15] tracking-[-0.015em] text-white sm:text-4xl md:text-5xl text-balance">
            {t("finalCta.titleA")} <span className="gradient-text">{t("finalCta.titleHl")}</span>
          </h2>
          <p className="mt-4 text-[15px] text-white/70 sm:text-lg">{t("finalCta.subtitle")}</p>
          <div className="mt-7 flex flex-col items-stretch justify-center gap-3 sm:mt-8 sm:flex-row sm:items-center">
            <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow text-base px-7 py-6 transition-transform duration-300 hover:scale-[1.03]">
              <Link to="/signup">
                {t("finalCta.primary")} <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white text-base px-7 py-6 transition-transform duration-300 hover:scale-[1.03]">
              <a href="#demo">{t("finalCta.secondary")}</a>
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
